// Two renderers from one AvatarData: an SVG portrait and a small canvas sprite.

import { ITEM_BY_ID, type AvatarData, type Item } from './model';
import { drawArt, drawTinted, hasArt } from '@kit/assets';
import { paintedCanvas, PAINTED_FEET, PW, PH } from './painted';

/**
 * Her portrait as an element: the painted storybook figure when its layers are loaded,
 * otherwise the SVG drawing. `crop` 'face' shows head and shoulders (top bar).
 */
export function avatarEl(a: AvatarData, size: number, crop: 'full' | 'face' = 'full'): HTMLElement {
  const dpr = window.devicePixelRatio || 1;
  const painted = paintedCanvas(a, crop === 'face' ? size * 2.4 * dpr : size * dpr);
  if (painted) {
    const img = document.createElement('img');
    img.src = painted.toDataURL('image/png');
    img.alt = '';
    if (crop === 'face') {
      // the head sits in the top third of the frame; show it filling the circle
      // flex-shrink 0: the wrapper is a flex box and would otherwise squeeze the whole body in
      img.style.cssText = `width:${size * 2.4}px;min-width:${size * 2.4}px;height:${size * 3.6}px;max-width:none;flex-shrink:0;margin-top:${-size * 0.3}px;display:block`;
      const wrap = document.createElement('div');
      wrap.style.cssText = `width:${size}px;height:${size}px;overflow:hidden;display:flex;justify-content:center;align-items:flex-start`;
      wrap.append(img);
      return wrap;
    }
    img.style.cssText = `width:${size}px;height:${size * 1.3}px;object-fit:contain;display:block;margin:0 auto`;
    return img;
  }
  const t = document.createElement('template');
  t.innerHTML = portraitSvg(a, size).trim();
  return t.content.firstElementChild as HTMLElement;
}

const OUTLINE = '#2a2a2e';

// ---------- Portrait (SVG string, viewBox 0 0 100 130) ----------

export function portraitSvg(a: AvatarData, size = 160): string {
  const top = ITEM_BY_ID[a.top];
  const bottom = ITEM_BY_ID[a.bottom];
  const shoes = ITEM_BY_ID[a.shoes];
  const hat = a.hat ? ITEM_BY_ID[a.hat] : undefined;
  const acc = a.accessory ? ITEM_BY_ID[a.accessory] : undefined;
  const back = a.back ? ITEM_BY_ID[a.back] : undefined;
  const bw = a.body.shape === 'a' ? 0 : a.body.shape === 'b' ? 3 : -2; // body width tweak
  const skin = a.body.skin;
  const hc = a.hair.color;

  const parts: string[] = [];
  // back items
  if (back) parts.push(backSvg(back, bw));
  // hair behind head
  parts.push(hairBack(a.hair.style, hc));
  // legs
  parts.push(bottomSvg(bottom, bw, skin));
  parts.push(shoesSvg(shoes));
  // body
  parts.push(topSvg(top, bw, skin));
  // neck + head
  parts.push(`<rect x="46" y="54" width="8" height="8" fill="${skin}"/>`);
  parts.push(`<circle cx="50" cy="38" r="22" fill="${skin}"/>`);
  // ears
  parts.push(`<circle cx="28" cy="40" r="4" fill="${skin}"/><circle cx="72" cy="40" r="4" fill="${skin}"/>`);
  // face
  parts.push(eyesSvg(a.eyes));
  parts.push(mouthSvg(a.mouth));
  parts.push(`<circle cx="36" cy="46" r="3" fill="#f2a1a1" opacity=".6"/><circle cx="64" cy="46" r="3" fill="#f2a1a1" opacity=".6"/>`);
  // hair front
  parts.push(hairFront(a.hair.style, hc));
  if (acc) parts.push(accessorySvg(acc));
  if (hat) parts.push(hatSvg(hat));
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 130" width="${size}" height="${Math.round(size * 1.3)}">${parts.join('')}</svg>`;
}

function hairBack(style: string, c: string): string {
  switch (style) {
    case 'long': return `<rect x="26" y="30" width="48" height="50" rx="12" fill="${c}"/>`;
    case 'ponytail': return `<ellipse cx="74" cy="56" rx="7" ry="18" fill="${c}"/>`;
    case 'braids': return `<rect x="24" y="36" width="8" height="40" rx="4" fill="${c}"/><rect x="68" y="36" width="8" height="40" rx="4" fill="${c}"/>`;
    case 'curly': return `<circle cx="30" cy="36" r="9" fill="${c}"/><circle cx="70" cy="36" r="9" fill="${c}"/><circle cx="32" cy="50" r="8" fill="${c}"/><circle cx="68" cy="50" r="8" fill="${c}"/>`;
    case 'bob': return `<rect x="27" y="28" width="46" height="34" rx="12" fill="${c}"/>`;
    default: return '';
  }
}
function hairFront(style: string, c: string): string {
  const cap = `<path d="M28 36 Q30 14 50 14 Q70 14 72 36 Q60 28 50 30 Q40 28 28 36Z" fill="${c}"/>`;
  switch (style) {
    case 'short': return cap;
    case 'bob': return `<path d="M28 40 Q28 14 50 14 Q72 14 72 40 L72 30 Q60 24 44 28 Q34 32 28 40Z" fill="${c}"/>`;
    case 'long': return `<path d="M28 40 Q28 14 50 14 Q72 14 72 40 L72 32 Q58 22 40 30 Q32 34 28 40Z" fill="${c}"/>`;
    case 'ponytail': return cap;
    case 'curly': return `<circle cx="36" cy="22" r="9" fill="${c}"/><circle cx="50" cy="17" r="10" fill="${c}"/><circle cx="64" cy="22" r="9" fill="${c}"/><circle cx="28" cy="30" r="7" fill="${c}"/><circle cx="72" cy="30" r="7" fill="${c}"/>`;
    case 'buns': return cap + `<circle cx="30" cy="20" r="8" fill="${c}"/><circle cx="70" cy="20" r="8" fill="${c}"/>`;
    case 'spiky': return `<path d="M28 36 L32 14 L38 28 L44 10 L50 26 L56 10 L62 28 L68 14 L72 36 Q50 26 28 36Z" fill="${c}"/>`;
    case 'braids': return `<path d="M28 40 Q28 14 50 14 Q72 14 72 40 L72 34 Q50 22 28 34Z" fill="${c}"/>`;
    default: return cap;
  }
}
function eyesSvg(style: string): string {
  const L = 41, R = 59, Y = 37;
  switch (style) {
    case 'happy': return `<path d="M${L - 4} ${Y + 1} Q${L} ${Y - 5} ${L + 4} ${Y + 1}" stroke="${OUTLINE}" stroke-width="2.5" fill="none"/><path d="M${R - 4} ${Y + 1} Q${R} ${Y - 5} ${R + 4} ${Y + 1}" stroke="${OUTLINE}" stroke-width="2.5" fill="none"/>`;
    case 'wide': return `<circle cx="${L}" cy="${Y}" r="5" fill="#fff"/><circle cx="${R}" cy="${Y}" r="5" fill="#fff"/><circle cx="${L}" cy="${Y}" r="3" fill="${OUTLINE}"/><circle cx="${R}" cy="${Y}" r="3" fill="${OUTLINE}"/>`;
    case 'wink': return `<circle cx="${L}" cy="${Y}" r="2.6" fill="${OUTLINE}"/><path d="M${R - 4} ${Y} Q${R} ${Y + 3} ${R + 4} ${Y}" stroke="${OUTLINE}" stroke-width="2.5" fill="none"/>`;
    case 'sleepy': return `<path d="M${L - 4} ${Y} Q${L} ${Y + 3} ${L + 4} ${Y}" stroke="${OUTLINE}" stroke-width="2.5" fill="none"/><path d="M${R - 4} ${Y} Q${R} ${Y + 3} ${R + 4} ${Y}" stroke="${OUTLINE}" stroke-width="2.5" fill="none"/>`;
    case 'star': return star(L, Y, 4, OUTLINE) + star(R, Y, 4, OUTLINE);
    default: return `<circle cx="${L}" cy="${Y}" r="2.6" fill="${OUTLINE}"/><circle cx="${R}" cy="${Y}" r="2.6" fill="${OUTLINE}"/>`;
  }
}
function star(cx: number, cy: number, r: number, fill: string): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r * 0.45 : r;
    pts.push(`${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`);
  }
  return `<polygon points="${pts.join(' ')}" fill="${fill}"/>`;
}
function mouthSvg(style: string): string {
  switch (style) {
    case 'grin': return `<path d="M42 47 Q50 56 58 47Z" fill="${OUTLINE}"/><path d="M44 48 Q50 52 56 48Z" fill="#fff"/>`;
    case 'open': return `<ellipse cx="50" cy="49" rx="4" ry="5" fill="${OUTLINE}"/>`;
    case 'smirk': return `<path d="M44 48 Q50 52 57 46" stroke="${OUTLINE}" stroke-width="2.2" fill="none" stroke-linecap="round"/>`;
    default: return `<path d="M43 47 Q50 54 57 47" stroke="${OUTLINE}" stroke-width="2.2" fill="none" stroke-linecap="round"/>`;
  }
}
function topSvg(t: Item, bw: number, skin: string): string {
  const c = t.color || '#888', c2 = t.color2 || '#fff';
  const x = 30 - bw, w = 40 + bw * 2;
  const arms = `<rect x="${x - 8}" y="62" width="9" height="26" rx="4" fill="${c}"/><rect x="${x + w - 1}" y="62" width="9" height="26" rx="4" fill="${c}"/>` +
    `<circle cx="${x - 3.5}" cy="90" r="4.5" fill="${skin}"/><circle cx="${x + w + 3.5}" cy="90" r="4.5" fill="${skin}"/>`;
  let body = `<rect x="${x}" y="60" width="${w}" height="32" rx="6" fill="${c}"/>`;
  switch (t.style) {
    case 'stripes': body += [66, 74, 82].map(y => `<rect x="${x}" y="${y}" width="${w}" height="4" fill="${c2}"/>`).join(''); break;
    case 'hoodie': body += `<path d="M${x + 6} 60 Q50 72 ${x + w - 6} 60 Q50 66 ${x + 6} 60Z" fill="${shade(c)}"/><rect x="${x + w / 2 - 8}" y="80" width="16" height="8" rx="2" fill="${shade(c)}"/>`; break;
    case 'overalls': body = `<rect x="${x}" y="60" width="${w}" height="32" rx="6" fill="${c2}"/><rect x="${x + 6}" y="70" width="${w - 12}" height="22" rx="3" fill="${c}"/><rect x="${x + 8}" y="60" width="4" height="12" fill="${c}"/><rect x="${x + w - 12}" y="60" width="4" height="12" fill="${c}"/>`; break;
    case 'dress': body = `<path d="M${x} 60 H${x + w} L${x + w + 8} 100 H${x - 8}Z" fill="${c}"/>`; break;
    case 'dress-stars': body = `<path d="M${x} 60 H${x + w} L${x + w + 8} 100 H${x - 8}Z" fill="${c}"/>` + star(42, 76, 3, c2) + star(56, 86, 3, c2) + star(48, 94, 2.5, c2); break;
    case 'coat': body = `<rect x="${x - 2}" y="60" width="${w + 4}" height="38" rx="5" fill="${c}"/><rect x="${x + w / 2 - 1}" y="60" width="2" height="38" fill="${c2}"/><rect x="${x + 4}" y="78" width="10" height="8" fill="${c2}"/>`; break;
    case 'vest': body += `<rect x="${x + 3}" y="60" width="${w - 6}" height="30" rx="4" fill="${c2}" opacity=".35"/><rect x="${x + 6}" y="74" width="9" height="8" fill="${shade(c)}"/><rect x="${x + w - 15}" y="74" width="9" height="8" fill="${shade(c)}"/>`; break;
  }
  return arms + body;
}
function bottomSvg(b: Item, bw: number, _skin: string): string {
  const c = b.color || '#555', c2 = b.color2 || '#fff';
  const x = 32 - bw, w = 36 + bw * 2;
  switch (b.style) {
    case 'shorts': return `<rect x="${x}" y="88" width="${w / 2 - 1}" height="14" rx="3" fill="${c}"/><rect x="${x + w / 2 + 1}" y="88" width="${w / 2 - 1}" height="14" rx="3" fill="${c}"/><rect x="${x + 2}" y="100" width="${w / 2 - 5}" height="14" fill="${_skin}"/><rect x="${x + w / 2 + 3}" y="100" width="${w / 2 - 5}" height="14" fill="${_skin}"/>`;
    case 'skirt': return `<path d="M${x} 88 H${x + w} L${x + w + 6} 106 H${x - 6}Z" fill="${c}"/><rect x="${x + 4}" y="104" width="${w / 2 - 7}" height="10" fill="${_skin}"/><rect x="${x + w / 2 + 3}" y="104" width="${w / 2 - 7}" height="10" fill="${_skin}"/>`;
    case 'skirt-stripes': return `<path d="M${x} 88 H${x + w} L${x + w + 6} 106 H${x - 6}Z" fill="${c}"/><path d="M${x + 8} 88 H${x + 16} L${x + 16} 106 H${x + 5}Z" fill="${c2}"/><path d="M${x + w - 16} 88 H${x + w - 8} L${x + w - 5} 106 H${x + w - 16}Z" fill="${c2}"/><rect x="${x + 4}" y="104" width="${w / 2 - 7}" height="10" fill="${_skin}"/><rect x="${x + w / 2 + 3}" y="104" width="${w / 2 - 7}" height="10" fill="${_skin}"/>`;
    case 'cargo': return `<rect x="${x}" y="88" width="${w / 2 - 1}" height="26" rx="3" fill="${c}"/><rect x="${x + w / 2 + 1}" y="88" width="${w / 2 - 1}" height="26" rx="3" fill="${c}"/><rect x="${x + 2}" y="98" width="8" height="7" fill="${shade(c)}"/><rect x="${x + w - 10}" y="98" width="8" height="7" fill="${shade(c)}"/>`;
    default: return `<rect x="${x}" y="88" width="${w / 2 - 1}" height="26" rx="3" fill="${c}"/><rect x="${x + w / 2 + 1}" y="88" width="${w / 2 - 1}" height="26" rx="3" fill="${c}"/>`;
  }
}
function shoesSvg(s: Item): string {
  const c = s.color || '#fff';
  if (s.style === 'boots') return `<rect x="30" y="106" width="17" height="16" rx="4" fill="${c}"/><rect x="53" y="106" width="17" height="16" rx="4" fill="${c}"/>`;
  return `<rect x="30" y="112" width="18" height="10" rx="5" fill="${c}"/><rect x="52" y="112" width="18" height="10" rx="5" fill="${c}"/><rect x="30" y="118" width="18" height="3" fill="${OUTLINE}" opacity=".25"/><rect x="52" y="118" width="18" height="3" fill="${OUTLINE}" opacity=".25"/>`;
}
function hatSvg(h: Item): string {
  const c = h.color || '#555', c2 = h.color2 || '#fff';
  switch (h.style) {
    case 'cap': return `<path d="M28 28 Q50 4 72 28Z" fill="${c}"/><rect x="26" y="26" width="34" height="5" rx="2" fill="${shade(c)}"/>`;
    case 'beanie': return `<path d="M27 32 Q50 2 73 32Z" fill="${c}"/><rect x="26" y="28" width="48" height="7" rx="3" fill="${shade(c)}"/><circle cx="50" cy="10" r="4" fill="${shade(c)}"/>`;
    case 'crown': return `<path d="M30 28 L30 12 L40 20 L50 6 L60 20 L70 12 L70 28Z" fill="${c}"/><circle cx="50" cy="14" r="2.5" fill="#e2574c"/><circle cx="36" cy="22" r="2" fill="#4f8fe6"/><circle cx="64" cy="22" r="2" fill="#4f8fe6"/>`;
    case 'brim': return `<path d="M30 26 Q50 6 70 26Z" fill="${c}"/><rect x="18" y="24" width="64" height="6" rx="3" fill="${shade(c)}"/><rect x="30" y="20" width="40" height="4" fill="#5c3a21"/>`;
    case 'wizard': return `<path d="M26 30 L50 -8 L74 30Z" fill="${c}"/><rect x="20" y="27" width="60" height="6" rx="3" fill="${shade(c)}"/>${star(50, 14, 4, c2)}`;
    default: return '';
  }
}
function accessorySvg(a: Item): string {
  const c = a.color || '#333';
  switch (a.style) {
    case 'glasses': return `<circle cx="41" cy="37" r="7" stroke="${c}" stroke-width="2" fill="none"/><circle cx="59" cy="37" r="7" stroke="${c}" stroke-width="2" fill="none"/><line x1="48" y1="37" x2="52" y2="37" stroke="${c}" stroke-width="2"/>`;
    case 'goggles': return `<circle cx="41" cy="37" r="8" stroke="${c}" stroke-width="3" fill="#bfe8ff" opacity=".9"/><circle cx="59" cy="37" r="8" stroke="${c}" stroke-width="3" fill="#bfe8ff" opacity=".9"/><rect x="47" y="35" width="6" height="4" fill="${c}"/>`;
    case 'bow': return `<path d="M62 18 L74 12 L74 24Z" fill="${c}"/><path d="M62 18 L50 12 L50 24Z" fill="${c}"/><circle cx="62" cy="18" r="3" fill="${shade(c)}"/>`;
    case 'scarf': return `<rect x="30" y="56" width="40" height="9" rx="4" fill="${c}"/><rect x="58" y="60" width="9" height="20" rx="3" fill="${c}"/>`;
    default: return '';
  }
}
function backSvg(b: Item, bw: number): string {
  const c = b.color || '#555';
  switch (b.style) {
    case 'cape': return `<path d="M${30 - bw} 60 Q50 64 ${70 + bw} 60 L${78 + bw} 112 Q50 118 ${22 - bw} 112Z" fill="${c}"/>`;
    case 'wings': return `<path d="M30 66 Q6 50 10 80 Q16 96 34 86Z" fill="${c}" stroke="#d9d4c7" stroke-width="1"/><path d="M70 66 Q94 50 90 80 Q84 96 66 86Z" fill="${c}" stroke="#d9d4c7" stroke-width="1"/>`;
    case 'backpack': return `<rect x="${22 - bw}" y="62" width="14" height="26" rx="5" fill="${c}"/><rect x="${64 + bw}" y="62" width="14" height="26" rx="5" fill="${c}"/>`;
    default: return '';
  }
}
function shade(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.max(0, ((n >> 16) & 255) - 40), g = Math.max(0, ((n >> 8) & 255) - 40), b = Math.max(0, (n & 255) - 40);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ---------- Sprite (canvas), 24 x 32 logical units ----------

export type Facing = 'down' | 'up' | 'left' | 'right';

const DIR_ROW: Record<Facing, number> = { down: 0, up: 1, left: 2, right: 3 };

/**
 * Painted sprite sheets (ART.md section B): one 4x4 sheet per layer, 48x64 cells.
 * Used when the body layer exists; otherwise the procedural sprite below.
 */
function drawSpriteArt(ctx: CanvasRenderingContext2D, a: AvatarData, x: number, y: number, scale: number, frame: number, facing: Facing): boolean {
  const bodyId = `avatar.body.${a.body.shape}.sprite`;
  if (!hasArt(bodyId)) return false;
  const w = 24 * scale, h = 32 * scale;
  const f = { index: frame % 4, count: 4, row: DIR_ROW[facing], rows: 4 };
  const dx = x - w / 2, dy = y - h;
  const back = a.back ? `avatar.item.${a.back}.sprite` : null;
  if (back && facing !== 'up') drawArt(ctx, back, dx, dy, w, h, f);
  drawTinted(ctx, bodyId, a.body.skin, dx, dy, w, h, f);
  drawArt(ctx, `avatar.item.${a.bottom}.sprite`, dx, dy, w, h, f);
  drawArt(ctx, `avatar.item.${a.shoes}.sprite`, dx, dy, w, h, f);
  drawArt(ctx, `avatar.item.${a.top}.sprite`, dx, dy, w, h, f);
  drawTinted(ctx, `avatar.hair.${a.hair.style}.sprite`, a.hair.color, dx, dy, w, h, f);
  if (a.accessory) drawArt(ctx, `avatar.item.${a.accessory}.sprite`, dx, dy, w, h, f);
  if (a.hat) drawArt(ctx, `avatar.item.${a.hat}.sprite`, dx, dy, w, h, f);
  if (back && facing === 'up') drawArt(ctx, back, dx, dy, w, h, f);
  return true;
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  a: AvatarData,
  x: number,          // center x in CSS px
  y: number,          // feet y in CSS px
  scale: number,      // px per unit
  frame = 0,          // walk frame 0..3; 0 = standing
  facing: Facing = 'down',
) {
  if (drawSpriteArt(ctx, a, x, y, scale, frame, facing)) return;
  if (drawPortraitSprite(ctx, a, x, y, scale, frame, facing)) return;
  drawPixelSprite(ctx, a, x, y, scale, frame, facing);
}

// ---------- Portrait as the walking figure ----------
// The walking figure is the Studio portrait itself, drawn small, so every choice she makes in
// the Studio (eyes, mouth, glasses, bows, scarves, every hair style) shows up as she walks.
// Movement is a hop, a lean into the step and a flip for left and right.

const portraitCache = new Map<string, HTMLImageElement>();
const MAX_CACHE = 12;

function portraitImage(a: AvatarData): HTMLImageElement | null {
  const key = JSON.stringify(a);
  let img = portraitCache.get(key);
  if (!img) {
    img = new Image();
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(portraitSvg(a, 200));
    portraitCache.set(key, img);
    if (portraitCache.size > MAX_CACHE) portraitCache.delete(portraitCache.keys().next().value!);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}

function drawPortraitSprite(ctx: CanvasRenderingContext2D, a: AvatarData, x: number, y: number, scale: number, frame: number, facing: Facing): boolean {
  // painted storybook figure when its layers are loaded, the SVG portrait otherwise
  const painted = paintedCanvas(a, Math.round(28 * scale * (window.devicePixelRatio || 1)));
  const img: CanvasImageSource | null = painted ?? portraitImage(a);
  if (!img) return false;
  // painted frame is 1024 x 1536 with feet near the bottom; the SVG is 100 x 130, feet at 122
  const h = painted ? 44 * scale : 40 * scale;
  const w = painted ? h * (PW / PH) : h * (100 / 130);
  const feet = painted ? PAINTED_FEET : 122 / 130;
  const walking = frame % 2 === 1;
  const hop = walking ? -scale * 1.2 : 0;
  const lean = frame === 1 ? 0.06 : frame === 3 ? -0.06 : 0;
  ctx.save();
  ctx.translate(x, y + hop);
  ctx.rotate(facing === 'left' ? -lean : lean);
  if (facing === 'left') ctx.scale(-1, 1);
  // a soft squash on landing frames
  if (frame === 2) ctx.scale(1.03, 0.97);
  ctx.drawImage(img, -w / 2, -h * feet, w, h);
  ctx.restore();
  return true;
}

function drawPixelSprite(ctx: CanvasRenderingContext2D, a: AvatarData, x: number, y: number, scale: number, frame: number, facing: Facing) {
  const top = ITEM_BY_ID[a.top], bottom = ITEM_BY_ID[a.bottom], shoes = ITEM_BY_ID[a.shoes];
  const hat = a.hat ? ITEM_BY_ID[a.hat] : undefined;
  const back = a.back ? ITEM_BY_ID[a.back] : undefined;
  const u = scale;
  const legSwing = frame === 1 ? 2 : frame === 3 ? -2 : 0;
  const bob = frame % 2 === 1 ? -0.5 : 0;
  ctx.save();
  ctx.translate(x, y + bob * u);
  const rect = (rx: number, ry: number, rw: number, rh: number, c: string) => { ctx.fillStyle = c; ctx.fillRect(rx * u, ry * u, rw * u, rh * u); };
  const circ = (cx: number, cy: number, r: number, c: string) => { ctx.fillStyle = c; ctx.beginPath(); ctx.arc(cx * u, cy * u, r * u, 0, Math.PI * 2); ctx.fill(); };
  // back item
  if (back?.style === 'cape') rect(-6, -20, 12, 16, back.color!);
  if (back?.style === 'wings') { circ(-8, -16, 4, back.color!); circ(8, -16, 4, back.color!); }
  if (back?.style === 'backpack' && facing !== 'down') rect(-4, -19, 8, 8, back.color!);
  // legs
  const legC = bottom.color || '#555';
  const isSkirt = bottom.style?.startsWith('skirt') || top.style?.startsWith('dress');
  if (isSkirt) {
    const c = top.style?.startsWith('dress') ? top.color! : legC;
    ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(-6 * u, -12 * u); ctx.lineTo(6 * u, -12 * u); ctx.lineTo(8 * u, -4 * u); ctx.lineTo(-8 * u, -4 * u); ctx.closePath(); ctx.fill();
    rect(-4 + legSwing * 0.5, -5, 3, 4, a.body.skin); rect(1 - legSwing * 0.5, -5, 3, 4, a.body.skin);
  } else {
    rect(-5, -10 + legSwing * 0.5, 4, 9 - legSwing * 0.5, legC);
    rect(1, -10 - legSwing * 0.5, 4, 9 + legSwing * 0.5, legC);
  }
  // shoes
  rect(-6, -2 + legSwing * 0.5, 5, 2, shoes.color || '#fff');
  rect(1, -2 - legSwing * 0.5, 5, 2, shoes.color || '#fff');
  // torso
  const tc = top.color || '#888';
  rect(-6, -21, 12, 11, tc);
  if (top.style === 'stripes') { rect(-6, -18, 12, 1.5, top.color2!); rect(-6, -14, 12, 1.5, top.color2!); }
  if (top.style === 'overalls') { rect(-6, -21, 12, 11, top.color2!); rect(-4, -17, 8, 7, tc); }
  if (top.style === 'coat') rect(-0.5, -21, 1, 11, top.color2!);
  // arms
  rect(-8, -20 - legSwing * 0.3, 2, 8, tc); rect(6, -20 + legSwing * 0.3, 2, 8, tc);
  rect(-8, -12 - legSwing * 0.3, 2, 2, a.body.skin); rect(6, -12 + legSwing * 0.3, 2, 2, a.body.skin);
  // head
  circ(0, -26, 6, a.body.skin);
  // hair
  const hc = a.hair.color;
  const st = a.hair.style;
  if (facing === 'up') { circ(0, -27, 6.2, hc); }
  else {
    ctx.fillStyle = hc; ctx.beginPath(); ctx.arc(0, -27 * u, 6.2 * u, Math.PI, 0); ctx.fill();
    if (st === 'bob' || st === 'long') { rect(-6.2, -27, 2, 6, hc); rect(4.2, -27, 2, 6, hc); }
    if (st === 'long') { rect(-6.5, -24, 2.5, 10, hc); rect(4, -24, 2.5, 10, hc); }
    if (st === 'buns') { circ(-6, -31, 2.2, hc); circ(6, -31, 2.2, hc); }
    if (st === 'ponytail') circ(facing === 'left' ? 6 : -6, -24, 2.4, hc);
    if (st === 'spiky') { for (let i = -2; i <= 2; i++) { ctx.beginPath(); ctx.moveTo(i * 2.4 * u, -31 * u); ctx.lineTo((i * 2.4 + 1.2) * u, -36 * u); ctx.lineTo((i * 2.4 + 2.4) * u, -31 * u); ctx.fill(); } }
    if (st === 'curly') { circ(-5, -31, 2.5, hc); circ(0, -33, 2.8, hc); circ(5, -31, 2.5, hc); }
    if (st === 'braids') { rect(-7, -27, 2, 12, hc); rect(5, -27, 2, 12, hc); }
    // eyes
    {
      const ex = facing === 'left' ? -1.5 : facing === 'right' ? 1.5 : 0;
      ctx.fillStyle = OUTLINE;
      if (facing !== 'right') ctx.fillRect((-2.5 + ex) * u, -26 * u, 1.2 * u, 1.4 * u);
      if (facing !== 'left') ctx.fillRect((1.3 + ex) * u, -26 * u, 1.2 * u, 1.4 * u);
    }
  }
  // hat
  if (hat) {
    const c = hat.color!;
    if (hat.style === 'cap') { rect(-6.5, -33, 13, 3, c); rect(facing === 'left' ? -10 : -3, -31, 8, 1.5, c); }
    else if (hat.style === 'beanie') { rect(-6.5, -34, 13, 4.5, c); circ(0, -34.5, 1.5, c); }
    else if (hat.style === 'crown') { rect(-5, -35, 10, 3.5, c); for (let i = -1; i <= 1; i++) circ(i * 3.5, -35.5, 1.2, c); }
    else if (hat.style === 'brim') { rect(-9, -32, 18, 2, c); rect(-5, -36, 10, 4, c); }
    else if (hat.style === 'wizard') { rect(-8, -32, 16, 2, c); ctx.fillStyle = c; ctx.beginPath(); ctx.moveTo(-5 * u, -32 * u); ctx.lineTo(0, -44 * u); ctx.lineTo(5 * u, -32 * u); ctx.fill(); }
  }
  ctx.restore();
}
